import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { LUX_EASE } from "@/components/Reveal";

export default function FAQ() {
  const { t } = useLanguage();
  usePageMeta(t.meta.faq.title, t.meta.faq.desc);
  const [open, setOpen] = useState(0);

  return (
    <main>
      <PageHero eyebrow={t.faqPage.eyebrow} title={t.faqPage.heading} body={t.faqPage.body} />

      <section className="pb-24 pt-4 md:pb-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {t.faqPage.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`faq-item-${i + 1}`}
                  className={`border-b hairline transition-colors duration-300 ${isOpen ? "border-gold/35" : ""}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-question-${i + 1}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-7 text-left"
                  >
                    <span
                      className={`font-serif text-xl transition-colors duration-300 sm:text-2xl ${
                        isOpen ? "text-gold-light" : "text-cream"
                      }`}
                    >
                      {item.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: LUX_EASE }}
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                        isOpen ? "border-gold bg-gold text-obsidian" : "border-gold/40 text-gold"
                      }`}
                    >
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: LUX_EASE }}
                        className="overflow-hidden"
                        data-testid={`faq-answer-${i + 1}`}
                      >
                        <p className="max-w-3xl pb-8 text-[15px] leading-relaxed text-cream/65">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
